import { useMemo, useRef, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useSponsors, trackClick, trackImpression } from "@/lib/useSponsors";
import crest from "../assets/logo.png";

const levelOf = (tier) => (tier === "plata" ? "silver" : tier === "bronce" ? "bronze" : "gold");

const LEVELS = {
  gold: { label: "Gold", divider: "via-gold/50", size: { logo: "h-20 sm:h-24", box: 100, w: 210, text: "text-2xl" } },
  silver: { label: "Silver", divider: "via-white/30", size: { logo: "h-14 sm:h-16", box: 76, w: 176, text: "text-xl" } },
  bronze: { label: "Bronze", divider: "via-white/20", size: { logo: "h-10 sm:h-12", box: 60, w: 150, text: "text-lg" } },
};

const mapSponsor = (s) => ({
  id: s.id, slug: s.slug, name: s.name, logo: s.logo_url, website: s.url,
  industry: s.category, tier: s.tier, level: levelOf(s.tier),
});

function useImpression(id, placement) {
  const seen = useRef(false);
  useEffect(() => {
    if (id && !seen.current) { seen.current = true; trackImpression(id, placement); }
  }, [id, placement]);
}

/* ---------- Logo flotante (sin card / sin borde / sin sombra) ---------- */
function FloatingLogo({ s, size, placement }) {
  useImpression(s.id, placement);
  const cls = "group relative flex flex-col items-center justify-start text-center shrink-0 snap-center rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-gold";
  const inner = (
    <>
      {/* halo dorado difuminado: aparece del centro hacia afuera al hacer hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[46px] h-44 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: "radial-gradient(ellipse at center, rgba(212,160,23,0.55) 0%, rgba(212,160,23,0.22) 42%, rgba(212,160,23,0) 72%)" }}
      />
      <div className="relative flex items-center justify-center" style={{ minHeight: size.box }}>
        {s.logo
          ? <img src={s.logo} alt={`Logo de ${s.name}`} loading="lazy" decoding="async"
              className={`${size.logo} w-auto object-contain will-change-transform transition-[transform,filter] duration-300 group-hover:scale-110 group-hover:brightness-110 group-hover:saturate-150`} />
          : <span className={`font-black text-white ${size.text}`} style={{ fontFamily: "Outfit" }}>{s.name}</span>}
      </div>
      <span className="mt-3 text-sm font-semibold text-white/90 transition-colors duration-300 group-hover:text-gold">{s.name}</span>
      <span aria-hidden className="mt-2 h-[3px] w-0 rounded-full bg-gold shadow-[0_0_12px_rgba(212,160,23,0.85)] transition-all duration-300 group-hover:w-10" />
    </>
  );
  return (
    <Link to={`/patrocinadores/${s.slug}`} onClick={() => trackClick(s.id)}
      title="Ver perfil" aria-label={`Ver perfil de ${s.name}`}
      data-testid={`sponsor-logo-${s.slug || s.id}`} className={cls} style={{ width: size.w }}>{inner}</Link>
  );
}

/* ---------- Fila por nivel: centrada si entra, carrusel si sobra ---------- */
function TierRow({ level, items, placement }) {
  const cfg = LEVELS[level];
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth > el.clientWidth + 8);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [items.length]);

  if (!items.length) return null;
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.75, behavior: "smooth" });

  return (
    <motion.div
      className="mt-14 first:mt-0" data-testid={`sponsor-tier-${level}`}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="flex items-center justify-center gap-3">
        <span className={`h-px w-12 bg-gradient-to-r from-transparent ${cfg.divider}`} />
        <span className="text-[11px] font-black uppercase tracking-[0.32em] text-gold">{cfg.label}</span>
        <span className={`h-px w-12 bg-gradient-to-l from-transparent ${cfg.divider}`} />
      </div>

      <div className="relative mt-9">
        {overflow && (
          <button onClick={() => scroll(-1)} aria-label="Anteriores" className="hidden sm:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        <div ref={ref} className={`flex items-start gap-x-10 sm:gap-x-16 overflow-x-auto no-scrollbar snap-x py-2 ${overflow ? "px-1 sm:px-12" : "justify-center"}`}>
          {items.map((s) => <FloatingLogo key={s.id} s={s} size={cfg.size} placement={placement} />)}
        </div>
        {overflow && (
          <button onClick={() => scroll(1)} aria-label="Siguientes" className="hidden sm:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </motion.div>
  );
}

export function SponsorCTA({ text = "¿Tu empresa quiere crecer junto al club?", label = "Convertite en Sponsor", href = "/patrocinadores" }) {
  const internal = href.startsWith("/");
  const btn = "group inline-flex items-center gap-2 rounded-xl bg-gold px-8 py-4 font-bold text-navy transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_34px_rgba(212,160,23,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white motion-safe:animate-[cta-pulse_3.2s_ease-in-out_infinite]";
  const inner = <>{label}<ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>;
  return (
    <div className="mt-16 text-center" data-testid="sponsor-cta">
      {text && <p className="text-white/70 text-base sm:text-lg">{text}</p>}
      <div className="mt-5">
        {internal
          ? <Link to={href} data-testid="sponsor-cta-btn" className={btn}>{inner}</Link>
          : <a href={href} target="_blank" rel="noopener noreferrer" data-testid="sponsor-cta-btn" className={btn}>{inner}</a>}
      </div>
    </div>
  );
}

function Backdrop() {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(212,160,23,0.14), transparent 55%)" }} />
      <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <img src={crest} alt="" aria-hidden className="absolute left-1/2 top-1/2 w-[560px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.05] grayscale" />
    </div>
  );
}

function EmptyState({ ctaHref }) {
  return (
    <div className="mt-16 mx-auto max-w-2xl text-center" data-testid="sponsor-empty">
      <p className="text-xl sm:text-2xl font-black text-white" style={{ fontFamily: "Outfit" }}>Este espacio puede ser para tu empresa.</p>
      <p className="mt-2 text-white/60">Sumate al club y llegá a toda nuestra comunidad.</p>
      <SponsorCTA href={ctaHref} text="" />
    </div>
  );
}

export function SponsorSection({
  placement = "wall",
  eyebrow = "Nuestros socios comerciales",
  title = "Ellos hacen posible el club",
  subtitle = "Cada logo es una empresa que elige apostar por nuestra comunidad. Gracias por bancar cada partido, cada pibe y cada sueño.",
  ctaHref = "/patrocinadores",
  ctaText,
  ctaLabel,
}) {
  const raw = useSponsors(placement);
  const grouped = useMemo(() => {
    const g = { gold: [], silver: [], bronze: [] };
    raw.map(mapSponsor).forEach((s) => g[s.level].push(s));
    return g;
  }, [raw]);
  const empty = raw.length === 0;

  return (
    <section aria-labelledby="sponsors-title" data-testid="sponsor-section" className="relative overflow-hidden bg-[#0B1F5B] text-white">
      <Backdrop />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <header className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-black uppercase tracking-[0.28em] text-gold">{eyebrow}</span>
          <h2 id="sponsors-title" className="mt-4 text-3xl sm:text-5xl font-black tracking-tight text-white" style={{ fontFamily: "Outfit" }}>{title}</h2>
          {subtitle && <p className="mt-4 text-white/60 text-base sm:text-lg">{subtitle}</p>}
        </header>

        {empty ? <EmptyState ctaHref={ctaHref} /> : (
          <>
            <div className="mt-14">
              <TierRow level="gold" items={grouped.gold} placement={placement} />
              <TierRow level="silver" items={grouped.silver} placement={placement} />
              <TierRow level="bronze" items={grouped.bronze} placement={placement} />
            </div>
            <SponsorCTA href={ctaHref} text={ctaText} label={ctaLabel} />
          </>
        )}
      </div>
    </section>
  );
}

export default SponsorSection;
